/**
 * Auth Debugger Utility
 * 
 * Helps monitor auth events and detect potential loops
 */

interface AuthEvent {
  timestamp: number;
  event: string;
  userId?: string;
  source: string;
}

class AuthDebugger {
  private events: AuthEvent[] = [];
  private readonly maxEvents = 100; // Keep last 100 events
  
  /**
   * Log an auth event
   */
  logEvent(event: string, source: string, userId?: string): void {
    const authEvent: AuthEvent = {
      timestamp: Date.now(),
      event,
      userId,
      source
    };
    
    this.events.push(authEvent);
    
    // Keep only the most recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }
    
    console.debug(`[AuthDebugger] ${source}: ${event}${userId ? ` (${userId})` : ''}`);
    
    // Check for potential loops
    this.detectLoops();
  }
  
  /**
   * Detect potential auth loops
   */
  private detectLoops(): void {
    const now = Date.now();
    const recentEvents = this.events.filter(e => now - e.timestamp < 30000); // Last 30 seconds
    
    // Count events by type
    const eventCounts: Record<string, number> = {};
    recentEvents.forEach(event => {
      eventCounts[event.event] = (eventCounts[event.event] || 0) + 1;
    });
    
    // Check for suspicious patterns
    Object.entries(eventCounts).forEach(([eventType, count]) => {
      if (count > 5) {
        console.warn(`[AuthDebugger] Potential loop detected: ${eventType} occurred ${count} times in 30 seconds`);
      }
    });
    
    // Check for rapid consecutive events of the same type
    const lastFiveEvents = recentEvents.slice(-5);
    if (lastFiveEvents.length === 5) {
      const allSameEvent = lastFiveEvents.every(e => e.event === lastFiveEvents[0].event);
      if (allSameEvent) {
        const timeSpan = lastFiveEvents[4].timestamp - lastFiveEvents[0].timestamp;
        if (timeSpan < 10000) { // 10 seconds
          console.error(`[AuthDebugger] LOOP DETECTED: ${lastFiveEvents[0].event} repeated 5 times in ${timeSpan}ms`);
        }
      }
    }
  }
  
  /**
   * Get recent events for debugging
   */
  getRecentEvents(minutes: number = 5): AuthEvent[] {
    const cutoff = Date.now() - (minutes * 60 * 1000);
    return this.events.filter(e => e.timestamp > cutoff);
  }
  
  /**
   * Get event statistics
   */
  getStats(): {
    totalEvents: number;
    eventTypes: Record<string, number>;
    sources: Record<string, number>;
    recentEvents: number;
  } {
    const now = Date.now();
    const recentCutoff = now - (5 * 60 * 1000); // 5 minutes
    
    const eventTypes: Record<string, number> = {};
    const sources: Record<string, number> = {};
    let recentEvents = 0;
    
    this.events.forEach(event => {
      eventTypes[event.event] = (eventTypes[event.event] || 0) + 1;
      sources[event.source] = (sources[event.source] || 0) + 1;
      
      if (event.timestamp > recentCutoff) {
        recentEvents++;
      }
    });
    
    return {
      totalEvents: this.events.length,
      eventTypes,
      sources,
      recentEvents
    };
  }
  
  /**
   * Clear all events
   */
  clear(): void {
    this.events = [];
    console.log('[AuthDebugger] Events cleared');
  }
  
  /**
   * Export events for analysis
   */
  exportEvents(): string {
    return JSON.stringify(this.events, null, 2);
  }
}

// Export singleton instance
export const authDebugger = new AuthDebugger();

// Convenience functions
export const logAuthEvent = (event: string, source: string, userId?: string) => 
  authDebugger.logEvent(event, source, userId);

export const getAuthStats = () => authDebugger.getStats();

export const getRecentAuthEvents = (minutes?: number) => 
  authDebugger.getRecentEvents(minutes);

export const clearAuthDebugger = () => authDebugger.clear();

export const exportAuthEvents = () => authDebugger.exportEvents();

// Make it available globally for debugging in console
if (typeof window !== 'undefined') {
  (window as any).authDebugger = {
    getStats: getAuthStats,
    getRecentEvents: getRecentAuthEvents,
    clear: clearAuthDebugger,
    export: exportAuthEvents
  };
} 